import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditBookAuthorMutation } from "@modules/bookAuthors/services/mutations";
import { BookAuthorFormValues, bookAuthorSchema } from "@modules/bookAuthors/validations";
import { listBookAuthorsQueryKey } from "@modules/bookAuthors/services/queries";

export function useEditBookAuthorModal(modalRef: RefObject<ModalRef>, onComplete: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<BookAuthorFormValues>({
    resolver: zodResolver(bookAuthorSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditBookAuthorMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listBookAuthorsQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("book_contributor_admin.success.edit"),
      });

      onComplete();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: BookAuthorFormValues) => {
    const itemId = modalRef.current?.getId();
    await mutateAsync({ id: itemId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
